import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { 
  Sparkles, 
  Heart, 
  Zap, 
  Clock, 
  Award,
  ChevronDown,
  Star,
  Leaf,
  UserCheck,
  Clipboard,
  CheckCircle2,
  ArrowRight,
  MapPin,
  Phone,
  Mail,
  Gift,
  AlertCircle
} from 'lucide-react';
import { 
  testimonials, 
  faqData, 
  services, 
  pricingPlans,
  whyChooseUs,
  journeySteps 
} from '../data/mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export const Home = () => {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    age: '',
    telephone: '',
    email: '',
    objectif: '',
    codePostal: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Call backend API
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    // Transform form data to match backend expectations
    const submitData = {
      ...formData,
      code_postal: formData.codePostal // Map frontend field to backend field
    };
    delete submitData.codePostal; // Remove frontend field name
    
    fetch(`${API_URL}/api/contact/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(submitData),
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else if (response.status === 422) {
          // Handle validation errors
          return response.json().then(errorData => {
            throw new Error(`Validation error: ${errorData.detail?.[0]?.msg || 'Invalid form data'}`);
          });
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      })
      .then(data => {
        if (data.success) {
          toast.success(data.message);
          setFormData({
            nom: '',
            prenom: '',
            age: '',
            telephone: '',
            email: '',
            objectif: '',
            codePostal: ''
          });
        } else {
          toast.error('Une erreur est survenue. Veuillez réessayer.');
        }
      })
      .catch(error => {
        console.error('Error:', error);
        toast.error('Une erreur est survenue. Veuillez réessayer ou nous contacter par WhatsApp.');
      });
  };

  const iconMap = {
    clipboard: Clipboard,
    userCheck: UserCheck,
    leaf: Leaf,
    sparkles: Sparkles
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://customer-assets.emergentagent.com/job_ems-lyon-sud/artifacts/7atlhud1_IMG_6615%202.jpg')`,
            }}
          />
          {/* Emerald green filter with transparency */}
          <div className="absolute inset-0 bg-emerald-600/50" />
          {/* Dark gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 via-emerald-800/70 to-emerald-700/50" />
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 bg-yellow-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-yellow-500/30">
              <MapPin className="w-4 h-4 text-yellow-500" />
              <span className="text-sm font-medium text-yellow-100">Oullins - À quelques minutes de Lyon</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Votre centre Wellness Esthétique & EMS à{' '}
              <span className="text-yellow-500">Oullins</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-emerald-50 mb-8 leading-relaxed">
              Révélez votre meilleure version dans un espace dédié au bien-être et à la technologie
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <a
                href="https://wa.me/33659839111"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-300 hover:shadow-2xl hover:scale-105"
              >
                Réserver mon bilan personnalisé
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <button
                onClick={() => document.getElementById('expertises').scrollIntoView({ behavior: 'smooth' })}
                className="inline-flex items-center justify-center bg-white/10 backdrop-blur-md hover:bg-white/20 text-white px-8 py-4 rounded-full font-semibold text-lg border border-white/30 transition-all duration-300"
              >
                Découvrir nos services
              </button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
              <div className="bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/20">
                <div className="flex items-center space-x-3">
                  <Zap className="w-8 h-8 text-yellow-500" />
                  <div>
                    <div className="text-2xl font-bold text-white">25 min</div>
                    <div className="text-xs text-emerald-100">Séances EMS</div>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/20">
                <div className="flex items-center space-x-3">
                  <Award className="w-8 h-8 text-yellow-500" />
                  <div>
                    <div className="text-2xl font-bold text-white">7+</div>
                    <div className="text-xs text-emerald-100">Soins esthétiques</div>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/20 col-span-2 sm:col-span-1">
                <div className="flex items-center space-x-3">
                  <Heart className="w-8 h-8 text-yellow-500" />
                  <div>
                    <div className="text-2xl font-bold text-white">100%</div>
                    <div className="text-xs text-emerald-100">Personnalisé</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/70" />
        </div>
      </section>

      {/* Problems Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-emerald-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Vous manquez de temps pour prendre soin de vous ?
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Nous comprenons vos défis : zones résistantes, relâchement cutané, 
              perte de tonicité, ou besoin de remise en forme. Notre approche combine 
              efficacité et bien-être pour des résultats visibles.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: 'Zones résistantes', desc: 'Graisses localisées difficiles à éliminer' },
              { title: 'Relâchement cutané', desc: 'Peau qui manque de fermeté et de tonicité' },
              { title: 'Manque de temps', desc: 'Emploi du temps chargé, besoin de solutions rapides' },
              { title: 'Perte de confiance', desc: 'Envie de retrouver une silhouette harmonieuse' }
            ].map((problem, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-emerald-100 hover:border-emerald-300 group"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-emerald-200 transition-colors duration-300">
                  <CheckCircle2 className="w-6 h-6 text-emerald-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{problem.title}</h3>
                <p className="text-sm text-gray-600">{problem.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertises Section */}
      <section id="expertises" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Nos Expertises
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Une approche unique combinant électrostimulation EMS et soins esthétiques avancés 
              pour une transformation complète
            </p>
          </div>

          {/* EMS Section */}
          <div className="mb-20">
            <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 rounded-3xl overflow-hidden">
              <div className="grid lg:grid-cols-2 gap-0">
                <div className="p-8 sm:p-12 flex flex-col justify-center">
                  <div className="inline-flex items-center space-x-2 bg-yellow-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6 w-fit">
                    <Zap className="w-5 h-5 text-yellow-500" />
                    <span className="text-sm font-medium text-yellow-100">Électrostimulation EMS</span>
                  </div>
                  <h3 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                    EMS à Oullins
                  </h3>
                  <p className="text-emerald-50 text-lg mb-8 leading-relaxed">
                    L'électrostimulation musculaire pour un renforcement profond et efficace. 
                    Des séances de 25 minutes seulement pour des résultats équivalents à plusieurs heures de sport.
                  </p>
                  <ul className="space-y-4 mb-8">
                    {services.ems[0].benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircle2 className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <span className="text-white">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center space-x-4">
                    <div className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
                      <span className="text-white font-semibold">⚡ 25 minutes</span>
                    </div>
                    <div className="bg-white/10 backdrop-blur-md px-4 py-2 rounded-full border border-white/20">
                      <span className="text-white font-semibold">🔥 Résultats rapides</span>
                    </div>
                  </div>
                </div>
                <div className="relative h-96 lg:h-auto">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_ems-lyon-sud/artifacts/pdadqtz3_IMG_0442.HEIC"
                    alt="Séance EMS"
                    className="w-full h-full object-cover"
                  />
                  {/* Green transparent overlay */}
                  <div className="absolute inset-0 bg-emerald-600/30" />
                </div>
              </div>
            </div>
          </div>

          {/* Aesthetic Services */}
          <div>
            <div className="text-center mb-12">
              <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Soins Esthétiques Avancés
              </h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Des technologies de pointe pour le remodelage corporel, le raffermissement 
                et l'amélioration de la texture cutanée
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.aesthetic.map((service, index) => (
                <div 
                  key={index}
                  className="bg-gradient-to-br from-white to-emerald-50/30 p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-emerald-100 hover:border-emerald-300 group"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Sparkles className="w-7 h-7 text-white" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">{service.name}</h4>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <ul className="space-y-2">
                    {service.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Before/After Gallery Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 via-white to-emerald-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Résultats de Nos Clientes
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Découvrez les transformations réelles obtenues grâce à nos programmes personnalisés
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Before/After Result 1 */}
            <div className="group relative bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 mx-auto">
              <div className="relative h-[500px] flex items-center justify-center bg-emerald-50">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ems-lyon-sud/artifacts/oivz29y9_e22b9cc7-cbeb-4dc2-aa26-1c3df8b68a0d.jpg"
                  alt="Transformation client 1"
                  className="w-full h-full object-contain"
                />
                
                {/* Before/After Labels */}
                <div className="absolute top-6 left-6">
                  <div className="inline-block bg-white/95 backdrop-blur-sm text-gray-900 px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    AVANT
                  </div>
                </div>
                <div className="absolute top-6 right-6">
                  <div className="inline-block bg-yellow-500 text-gray-900 px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    APRÈS
                  </div>
                </div>
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <div className="p-6 text-center">
                <p className="text-gray-700 font-medium">
                  Résultat suite à un protocole personnalisé
                </p>
              </div>
            </div>

            {/* Before/After Result 2 */}
            <div className="group relative bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 mx-auto">
              <div className="relative h-[500px] flex items-center justify-center bg-emerald-50">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ems-lyon-sud/artifacts/z7fwtais_24F4AF4C-56DB-41E1-9446-F82267EE491C%202.jpg"
                  alt="Transformation client 2"
                  className="w-full h-full object-contain"
                />
                
                {/* Before/After Labels */}
                <div className="absolute top-6 left-6">
                  <div className="inline-block bg-white/95 backdrop-blur-sm text-gray-900 px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    AVANT
                  </div>
                </div>
                <div className="absolute top-6 right-6">
                  <div className="inline-block bg-yellow-500 text-gray-900 px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                    APRÈS
                  </div>
                </div>
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <div className="p-6 text-center">
                <p className="text-gray-700 font-medium">
                  Résultats visibles avec un suivi régulier
                </p>
              </div>
            </div>

            {/* Before/After Result 3 - Perfect Stril */}
            <div className="group relative bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300 mx-auto">
              <div className="relative h-[500px] flex items-center justify-center bg-emerald-50">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ems-lyon-sud/artifacts/k994uh0d_6196BCF0-A4B7-4BF7-8D5D-C4B95C08B96D%202.JPG"
                  alt="Perfect Stril - Traitement des vergetures"
                  className="w-full h-full object-contain"
                />
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <div className="p-6 text-center">
                <div className="inline-block bg-emerald-600 text-white px-4 py-2 rounded-full text-sm font-semibold mb-3">
                  Perfect Stril
                </div>
                <p className="text-gray-700 font-medium">
                  Traitement des vergetures - Résultats après 20 jours
                </p>
              </div>
            </div>
          </div>

          {/* CTA Note */}
          <div className="mt-12 text-center bg-gradient-to-r from-emerald-700 to-emerald-600 p-8 rounded-2xl shadow-xl max-w-4xl mx-auto">
            <p className="text-white text-lg mb-6 font-medium">
              Ces résultats sont obtenus grâce à un programme personnalisé et un suivi régulier. 
              Chaque transformation est unique.
            </p>
            <a
              href="https://wa.me/33659839111"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
            >
              Commencez Votre Transformation
              <ArrowRight className="ml-2 w-6 h-6" />
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Pourquoi Choisir Notre Centre à Oullins ?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((reason, index) => {
              const IconComponent = iconMap[reason.icon];
              return (
                <div 
                  key={index}
                  className="text-center group"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{reason.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{reason.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Journey Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Votre Parcours de Transformation
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Un accompagnement personnalisé étape par étape pour atteindre vos objectifs
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              {journeySteps.map((step, index) => (
                <div 
                  key={index}
                  className="relative bg-gradient-to-br from-emerald-50 to-white p-8 rounded-2xl border-2 border-emerald-200 hover:border-emerald-400 transition-all duration-300 group"
                >
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg">
                    {step.step}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-2">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-700 to-emerald-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_ems-lyon-sud/artifacts/vdlu2b8y_33A62DD9-B385-4A65-AC06-F3939A3246A7%202.JPG')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Résultats Attendus
            </h2>
            <p className="text-xl text-emerald-50 max-w-3xl mx-auto">
              Une transformation visible et durable de votre corps et de votre bien-être
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {[
              { icon: Sparkles, title: 'Silhouette redessinée', desc: 'Corps plus tonique et harmonieux' },
              { icon: Heart, title: 'Peau plus ferme', desc: 'Raffermissement visible et durable' },
              { icon: Zap, title: 'Réduction cellulite', desc: 'Peau plus lisse et uniforme' },
              { icon: Award, title: 'Regain de confiance', desc: 'Bien-être physique et mental' }
            ].map((result, index) => (
              <div 
                key={index}
                className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 text-center group"
              >
                <result.icon className="w-12 h-12 text-yellow-500 mx-auto mb-4 group-hover:scale-110 transition-transform duration-300" />
                <h3 className="text-xl font-bold text-white mb-2">{result.title}</h3>
                <p className="text-emerald-100">{result.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Témoignages Clients
            </h2>
            <p className="text-lg text-gray-600">
              Découvrez les expériences de nos clientes à Oullins et Lyon Sud
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {testimonials.map((testimonial) => (
              <div 
                key={testimonial.id}
                className="bg-gradient-to-br from-emerald-50 to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-emerald-100"
              >
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>
                <div className="border-t border-emerald-200 pt-4">
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-emerald-600">{testimonial.location}</div>
                  <div className="text-sm text-gray-500 mt-1">{testimonial.service}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Trust Badge */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-3 rounded-full shadow-lg">
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              <span className="font-semibold">12 avis clients • 5/5 étoiles</span>
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="tarifs" className="py-20 bg-gradient-to-br from-gray-50 to-emerald-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Nos Tarifs EMS
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-4">
              Choisissez la formule adaptée à vos besoins et votre rythme
            </p>
            <div className="inline-flex items-center space-x-2 bg-yellow-100 px-4 py-2 rounded-full">
              <AlertCircle className="w-5 h-5 text-yellow-700" />
              <span className="text-sm font-medium text-yellow-800">
                Tenue EMS individuelle obligatoire : 30€ TTC
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
            {pricingPlans.map((planType, typeIndex) => (
              <div key={typeIndex} className="space-y-6">
                <div className={`text-center p-4 rounded-2xl ${
                  planType.engagement 
                    ? 'bg-gradient-to-br from-emerald-600 to-emerald-700 text-white' 
                    : 'bg-gradient-to-br from-gray-700 to-gray-800 text-white'
                }`}>
                  <h3 className="text-2xl font-bold mb-2">{planType.type}</h3>
                  {planType.engagement && (
                    <p className="text-emerald-100 text-sm">Renouvelable - Tarifs avantageux</p>
                  )}
                </div>

                {planType.plans.map((plan, planIndex) => (
                  <div 
                    key={planIndex}
                    className={`bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 ${
                      planType.engagement 
                        ? 'border-emerald-300 hover:border-emerald-500' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="text-center mb-6">
                      <div className="text-5xl font-bold text-gray-900 mb-2">
                        {plan.price}€
                        <span className="text-2xl text-gray-500">/mois</span>
                      </div>
                      <div className="text-emerald-700 font-semibold text-lg mb-1">
                        {plan.sessions} séances par mois
                      </div>
                      <div className="text-sm text-gray-500">
                        Soit {plan.perSession.toFixed(2)}€ / séance
                      </div>
                    </div>

                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Séances EMS de 25 minutes</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Suivi personnalisé</span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Bilan initial offert</span>
                      </li>
                      {planType.engagement && (
                        <>
                          <li className="flex items-start space-x-3">
                            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700 font-semibold">Économisez jusqu'à 80€/mois</span>
                          </li>
                          <li className="flex items-start space-x-3">
                            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700 text-sm">Prélèvement automatique obligatoire</span>
                          </li>
                        </>
                      )}
                    </ul>

                    <a
                      href="https://wa.me/33659839111"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`block w-full text-center py-3 rounded-full font-semibold transition-all duration-300 ${
                        planType.engagement
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          : 'bg-gray-700 hover:bg-gray-800 text-white'
                      }`}
                    >
                      Réserver
                    </a>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Payment Options */}
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="bg-white p-6 rounded-2xl shadow-lg border border-emerald-200">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-emerald-700 mr-2" />
                Paiement par Prélèvement Automatique
              </h4>
              <p className="text-gray-600 text-center text-sm leading-relaxed">
                Pour tous les <span className="font-semibold text-emerald-700">abonnements EMS avec engagement</span>, 
                le paiement par prélèvement automatique est obligatoire. 
                Ce mode de paiement garantit la continuité de votre suivi pour des résultats optimaux.
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-white p-6 rounded-2xl shadow-lg border-2 border-yellow-300">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
                💳 Options de Paiement avec ALMA
              </h4>
              <p className="text-gray-700 text-center text-sm leading-relaxed mb-3">
                Paiement fractionné en <span className="font-bold text-yellow-700">2, 3 ou 4 fois</span> disponible pour :
              </p>
              <div className="flex flex-wrap justify-center gap-3 mb-4">
                <span className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-medium">
                  ✓ Packs de séances EMS sans engagement
                </span>
                <span className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-medium">
                  ✓ Packs de soins esthétiques
                </span>
              </div>
              <p className="text-gray-600 text-center text-xs leading-relaxed">
                Les frais de dossier seront ajoutés selon le montant. 
                Demandez plus d'informations lors de votre réservation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Special Offer */}
      <section className="py-20 bg-gradient-to-br from-amber-50 via-emerald-50 to-amber-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle, #047857 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-emerald-700 p-10 sm:p-16 rounded-3xl shadow-2xl text-center border-4 border-yellow-500">
              <div className="inline-block bg-yellow-500 text-gray-900 px-6 py-2 rounded-full font-bold text-sm mb-6">
                <Gift className="inline-block w-5 h-5 mr-2" />
                OFFRE DÉCOUVERTE
              </div>
              
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Séance Découverte Offerte*
              </h2>
              
              <p className="text-xl text-emerald-50 mb-8 leading-relaxed">
                Profitez d'une séance découverte <span className="font-bold text-yellow-500">offerte</span> en cas de souscription 
                à un abonnement. Sinon, tarif exceptionnel de <span className="line-through">45€</span>{' '}
                <span className="font-bold text-yellow-500 text-2xl">25€</span> seulement !
              </p>

              <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 mb-8">
                <p className="text-emerald-100 text-sm leading-relaxed">
                  ⚠️ <span className="font-semibold">Nombre de places limité chaque semaine</span> - 
                  Réservez dès maintenant pour garantir votre créneau
                </p>
              </div>

              <a
                href="https://wa.me/33659839111"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 hover:shadow-2xl hover:scale-105"
              >
                Réserver ma séance découverte
                <ArrowRight className="ml-2 w-6 h-6" />
              </a>

              <p className="text-emerald-100 text-xs mt-6">
                * Offre valable pour les nouveaux clients uniquement
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Questions Fréquentes
            </h2>
            <p className="text-lg text-gray-600">
              Tout ce que vous devez savoir sur nos services EMS et soins esthétiques
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-2xl px-6 overflow-hidden"
                >
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-emerald-700 py-6 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-emerald-50 via-white to-emerald-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Contact Info */}
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                  Offrez à votre corps l'accompagnement qu'il mérite
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Contactez-nous pour un bilan personnalisé gratuit et découvrez comment 
                  nous pouvons vous aider à atteindre vos objectifs.
                </p>

                <div className="space-y-6 mb-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-emerald-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Adresse</h4>
                      <p className="text-gray-600">8 Boulevard Émile Zola<br />69600 Oullins</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-emerald-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Téléphone</h4>
                      <a href="tel:0659839111" className="text-emerald-700 hover:text-emerald-800 font-medium">
                        06 59 83 91 11
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-emerald-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                      <a href="mailto:thg.ems.bienetre@gmail.com" className="text-emerald-700 hover:text-emerald-800 font-medium break-all">
                        thg.ems.bienetre@gmail.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-emerald-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Horaires</h4>
                      <p className="text-gray-600">Sur rendez-vous uniquement<br />Lundi - Samedi</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-emerald-700 to-emerald-600 p-6 rounded-2xl text-white">
                  <h4 className="font-semibold text-lg mb-2">Réservation rapide par WhatsApp</h4>
                  <p className="text-emerald-50 text-sm mb-4">
                    Pour une réponse immédiate, contactez-nous directement sur WhatsApp
                  </p>
                  <a
                    href="https://wa.me/33659839111"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-white text-emerald-700 px-6 py-3 rounded-full font-semibold hover:bg-emerald-50 transition-all duration-300"
                  >
                    Ouvrir WhatsApp
                  </a>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white p-8 rounded-2xl shadow-xl border border-emerald-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Demande de Contact
                </h3>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nom" className="text-gray-700 font-medium mb-2 block">
                        Nom *
                      </Label>
                      <Input
                        id="nom"
                        name="nom"
                        value={formData.nom}
                        onChange={handleInputChange}
                        required
                        className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="prenom" className="text-gray-700 font-medium mb-2 block">
                        Prénom *
                      </Label>
                      <Input
                        id="prenom"
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleInputChange}
                        required
                        className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="age" className="text-gray-700 font-medium mb-2 block">
                        Âge
                      </Label>
                      <Input
                        id="age"
                        name="age"
                        type="number"
                        value={formData.age}
                        onChange={handleInputChange}
                        className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="codePostal" className="text-gray-700 font-medium mb-2 block">
                        Code Postal
                      </Label>
                      <Input
                        id="codePostal"
                        name="codePostal"
                        value={formData.codePostal}
                        onChange={handleInputChange}
                        className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="telephone" className="text-gray-700 font-medium mb-2 block">
                      Téléphone *
                    </Label>
                    <Input
                      id="telephone"
                      name="telephone"
                      type="tel"
                      value={formData.telephone}
                      onChange={handleInputChange}
                      required
                      className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium mb-2 block">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="objectif" className="text-gray-700 font-medium mb-2 block">
                      Votre Objectif *
                    </Label>
                    <Textarea
                      id="objectif"
                      name="objectif"
                      value={formData.objectif}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      placeholder="Décrivez brièvement vos objectifs et attentes..."
                      className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500 resize-none"
                    />
                  </div>

                  <Button 
                    type="submit"
                    className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-6 text-lg font-semibold rounded-xl transition-all duration-300 hover:shadow-lg"
                  >
                    Envoyer ma demande
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    * Champs obligatoires - Nous vous répondrons dans les 24h
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-emerald-950 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url('https://customer-assets.emergentagent.com/job_ems-lyon-sud/artifacts/9u7z7wt8_IMG_1286.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Prêt à Commencer Votre Transformation ?
          </h2>
          <p className="text-xl text-emerald-100 mb-10 max-w-3xl mx-auto leading-relaxed">
            Rejoignez nos nombreux clients satisfaits à Oullins et découvrez 
            l'excellence des soins wellness et EMS dans notre centre haut de gamme
          </p>
          <a
            href="https://wa.me/33659839111"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-10 py-5 rounded-full font-bold text-xl transition-all duration-300 hover:shadow-2xl hover:scale-105"
          >
            PRENDRE RENDEZ-VOUS À OULLINS
            <ArrowRight className="ml-3 w-6 h-6" />
          </a>
        </div>
      </section>

      <Footer />

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/33659839111"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 group"
        aria-label="Contactez-nous sur WhatsApp"
      >
        <svg
          className="w-8 h-8"
          fill="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
          1
        </span>
      </a>
    </div>
  );
};
